import React, { PropsWithChildren, useState } from 'react';
import {
  Outlet,
  HeadContent,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient as baseQueryClient } from '../queryClient';
import { ClerkProvider } from '@clerk/tanstack-react-start';
import { ConvexProvider, ConvexReactClient } from 'convex/react';
import { WorkforceProvider } from '../contexts/WorkforceContext';
import {
  ClerkAppAuthProvider,
  DisabledAppAuthProvider,
} from '../hooks/useAppRole';
import { ConvexClerkProvider } from '../components/site/ConvexClerkProvider';

export const Route = createRootRouteWithContext<{ queryClient: typeof baseQueryClient }>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Garderie Imaginette' },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  const [client] = useState(() => baseQueryClient);
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
  const convexUrl = import.meta.env.VITE_CONVEX_URL as string | undefined;
  const convexClient = React.useMemo(
    () => (convexUrl ? new ConvexReactClient(convexUrl) : null),
    [convexUrl]
  );

  return (
    <RootDocument>
      <Providers
        queryClient={client}
        publishableKey={publishableKey}
        convexClient={convexClient}
      >
        <Outlet />
      </Providers>
    </RootDocument>
  );
}

function Providers({
  children,
  queryClient,
  publishableKey,
  convexClient,
}: PropsWithChildren<{
  queryClient: typeof baseQueryClient;
  publishableKey?: string;
  convexClient: ConvexReactClient | null;
}>) {
  const appTree = (
    <QueryClientProvider client={queryClient}>
      <WorkforceProvider>{children}</WorkforceProvider>
    </QueryClientProvider>
  );

  // Clerk + Convex — punch kiosk still works signed-out (PIN); managers/employees
  // who sign in get a Convex JWT via the auth bridge.
  if (publishableKey && convexClient) {
    return (
      <ClerkProvider
        publishableKey={publishableKey}
        afterSignOutUrl="/"
        signInFallbackRedirectUrl="/portal"
        signUpFallbackRedirectUrl="/portal"
      >
        <ClerkAppAuthProvider>
          <ConvexClerkProvider client={convexClient}>{appTree}</ConvexClerkProvider>
        </ClerkAppAuthProvider>
      </ClerkProvider>
    );
  }

  if (publishableKey) {
    return (
      <ClerkProvider
        publishableKey={publishableKey}
        afterSignOutUrl="/"
        signInFallbackRedirectUrl="/portal"
        signUpFallbackRedirectUrl="/portal"
      >
        <ClerkAppAuthProvider>{appTree}</ClerkAppAuthProvider>
      </ClerkProvider>
    );
  }

  if (convexClient) {
    return (
      <DisabledAppAuthProvider>
        <ConvexProvider client={convexClient}>{appTree}</ConvexProvider>
      </DisabledAppAuthProvider>
    );
  }

  return <DisabledAppAuthProvider>{appTree}</DisabledAppAuthProvider>;
}

function RootDocument({ children }: PropsWithChildren) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
